import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Text,
  Alert,
  Modal,
  Progress,
  Group,
} from '@mantine/core';
import { IconAlertCircle, IconArrowRight } from '@tabler/icons-react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api/client';
import { BackendSwitcher } from '../components/BackendSwitcher';

// ─── Design tokens ────────────────────────────────────────────────────────────
const BG = '#09090B';
const CARD_BG = '#111113';
const BORDER = '#27272A';
const TEXT_PRIMARY = '#F4F4F5';
const TEXT_MUTED = '#71717A';
const BLUE = '#2563EB';

const inputStyles = {
  input: {
    background: 'transparent',
    borderColor: '#3F3F46',
    color: TEXT_PRIMARY,
    fontSize: 14,
    height: 44,
    borderRadius: 8,
    '&::placeholder': { color: '#52525B' },
    '&:focus': { borderColor: BLUE },
  },
  label: { color: TEXT_MUTED, marginBottom: 6, fontSize: 12, fontWeight: 500 },
};

// ─── Password strength ────────────────────────────────────────────────────────

function passwordStrength(pw: string) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Strong', 'Very strong'];
const STRENGTH_COLORS = ['', 'red', 'orange', 'yellow', 'teal'];

function ForcePasswordChangeModal({
  opened,
  onSuccess,
}: {
  opened: boolean;
  onSuccess: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const strength = passwordStrength(newPw);

  const handleSubmit = async () => {
    if (newPw.length < 8) return setError('Password must be at least 8 characters.');
    if (strength < 3) return setError('Password must contain uppercase, number, and special character.');
    if (newPw !== confirm) return setError('Passwords do not match.');

    setLoading(true);
    setError('');
    try {
      await authApi.changePassword({ current_password: current, new_password: newPw });
      onSuccess();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Failed to change password.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {}}
      withCloseButton={false}
      title="Change Your Password"
      centered
      size="sm"
      styles={{
        root: { zIndex: 9999 },
        content: { background: CARD_BG, border: `1px solid ${BORDER}` },
        header: { background: CARD_BG, color: TEXT_PRIMARY },
        title: { color: TEXT_PRIMARY, fontWeight: 700 },
        body: { background: CARD_BG },
      }}
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">You must change your password before continuing.</Text>
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} radius="sm">{error}</Alert>
        )}
        <PasswordInput label="Current Password" value={current} onChange={(e) => setCurrent(e.currentTarget.value)} styles={inputStyles} />
        <PasswordInput label="New Password" value={newPw} onChange={(e) => setNewPw(e.currentTarget.value)} styles={inputStyles} />
        {newPw && (
          <Box>
            <Progress value={(strength / 4) * 100} color={STRENGTH_COLORS[strength]} size="sm" radius="xl" />
            <Text size="xs" c={STRENGTH_COLORS[strength]} mt={4}>{STRENGTH_LABELS[strength]}</Text>
          </Box>
        )}
        <PasswordInput label="Confirm New Password" value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)} styles={inputStyles} />
        <Button fullWidth onClick={handleSubmit} loading={loading} mt="sm" style={{ background: BLUE, height: 44, borderRadius: 8, fontWeight: 600 }}>
          Update Password
        </Button>
      </Stack>
    </Modal>
  );
}

// ─── LoginPage ────────────────────────────────────────────────────────────────

export function LoginPage() {
  const navigate = useNavigate();
  const { login, signup, googleLogin, isAuthenticated } = useAuth();

  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [shopName, setShopName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // 20-tap-the-logo easter egg → open the backend switcher (works on prod
  // where the badge is hidden).
  const [backendOpen, setBackendOpen] = useState(false);
  const logoTapCount = useRef(0);
  const logoTapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleLogoClick = () => {
    logoTapCount.current += 1;
    if (logoTapTimer.current) clearTimeout(logoTapTimer.current);
    logoTapTimer.current = setTimeout(() => { logoTapCount.current = 0; }, 2000);
    if (logoTapCount.current >= 20) {
      logoTapCount.current = 0;
      setBackendOpen(true);
    }
  };

  const strength = mode === 'signup' ? passwordStrength(password) : 0;

  useEffect(() => {
    if (isAuthenticated) navigate('/shop/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) return null;

  // Decide where to go after a successful credential exchange.
  // Multi-shop users → /shop/select-shop; everyone else → /shop/dashboard.
  // Picker is also forced when memberships exist but there's no active shop pointer.
  const routeAfterLogin = async (me: { shop_id: number | null }) => {
    try {
      const shops = await authApi.myShops();
      if (shops.length > 1 || (shops.length >= 1 && me.shop_id == null)) {
        navigate('/shop/select-shop');
        return;
      }
    } catch {
      // backend might be older; fall through to dashboard
    }
    navigate('/shop/dashboard');
  };

  const handleLogin = async () => {
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      const me = await login({ email, password });
      await routeAfterLogin(me);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? (detail[0]?.msg ?? 'Invalid email or password.')
        : typeof detail === 'string'
        ? detail
        : 'Invalid email or password.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box style={{ minHeight: '100vh', background: BG, display: 'flex' }}>
      <BackendSwitcher position="top-right" open={backendOpen} onOpenChange={setBackendOpen} />
      <ForcePasswordChangeModal
        opened={showPasswordChange}
        onSuccess={async () => {
          try {
            const me = await authApi.me();
            await routeAfterLogin(me);
          } catch {
            navigate('/shop/dashboard');
          }
        }}
      />

      {/* Left: branding panel (hidden on mobile) */}
      <Box style={{
        flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        padding: 48, background: '#0C0C0E',
      }}
        className="login-brand-panel"
      >
        <img
          src="/images/haist/haist-logo.png"
          alt="HAIST"
          style={{ height: 40, filter: 'invert(1) brightness(2)', marginBottom: 24, cursor: 'pointer', userSelect: 'none' }}
          onClick={handleLogoClick}
        />
        <Text size="xl" fw={700} c={TEXT_PRIMARY} ta="center" mb={8}>
          AI-Powered Auto Shop
        </Text>
        <Text size="sm" c={TEXT_MUTED} ta="center" style={{ maxWidth: 300, lineHeight: 1.6 }}>
          Inspect. Diagnose. Quote. Report. All powered by AI for professional mechanics.
        </Text>
      </Box>

      {/* Right: sign-in form */}
      <Box style={{
        width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column',
        justifyContent: 'center', padding: '48px 40px',
        borderLeft: `1px solid ${BORDER}`,
      }}>
        {/* Tab toggle */}
        <Group gap={0} mb={24}>
          {(['signin', 'signup'] as const).map((m) => (
            <Box
              key={m}
              onClick={() => { setMode(m); setError(''); }}
              style={{
                flex: 1, padding: '10px 0', textAlign: 'center', cursor: 'pointer',
                borderBottom: `2px solid ${mode === m ? BLUE : BORDER}`,
                transition: 'all 0.15s',
              }}
            >
              <Text size="sm" fw={mode === m ? 700 : 500} c={mode === m ? TEXT_PRIMARY : TEXT_MUTED}>
                {m === 'signin' ? 'Sign in' : 'Create account'}
              </Text>
            </Box>
          ))}
        </Group>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} radius="sm" mb="md"
            styles={{ root: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }, message: { color: '#fca5a5' } }}>
            {error}
          </Alert>
        )}

        <Stack gap={16}>
          {mode === 'signup' && (
            <TextInput label="Name" placeholder="John Smith" value={name} onChange={(e) => setName(e.currentTarget.value)} styles={inputStyles} />
          )}
          <TextInput
            label="Email"
            placeholder="you@shop.com"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            onKeyDown={(e) => e.key === 'Enter' && mode === 'signin' && handleLogin()}
            styles={inputStyles}
          />
          {mode === 'signup' && (
            <>
              <TextInput label="Phone" placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} styles={inputStyles} />
              <TextInput label="Shop Name" placeholder="Optional — defaults to Your Name's Garage" value={shopName} onChange={(e) => setShopName(e.currentTarget.value)} styles={inputStyles} />
            </>
          )}
          <PasswordInput
            label="Password"
            placeholder={mode === 'signup' ? 'Min 8 characters' : 'Enter your password'}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            onKeyDown={(e) => e.key === 'Enter' && mode === 'signin' && handleLogin()}
            styles={inputStyles}
          />
          {mode === 'signup' && (
            <>
              {password && (
                <Box>
                  <Progress value={(strength / 4) * 100} color={STRENGTH_COLORS[strength]} size="sm" radius="xl" />
                  <Text size="xs" c={STRENGTH_COLORS[strength]} mt={4}>{STRENGTH_LABELS[strength]}</Text>
                </Box>
              )}
              <PasswordInput label="Confirm Password" placeholder="Re-enter password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.currentTarget.value)} styles={inputStyles} />
            </>
          )}
          <Button
            fullWidth
            loading={loading}
            rightSection={!loading && <IconArrowRight size={16} />}
            onClick={async () => {
              if (mode === 'signin') {
                handleLogin();
                return;
              }
              // Signup validation
              if (!name.trim()) return setError('Name is required.');
              if (!email.trim()) return setError('Email is required.');
              if (password.length < 8) return setError('Password must be at least 8 characters.');
              if (strength < 2) return setError('Password is too weak. Add uppercase, numbers, or special characters.');
              if (password !== confirmPassword) return setError('Passwords do not match.');

              setLoading(true);
              setError('');
              try {
                const me = await signup({
                  user_name: name.trim(),
                  user_email: email.trim(),
                  password,
                  phone: phone.trim() || undefined,
                  shop_name: shopName.trim() || undefined,
                });
                await routeAfterLogin(me);
              } catch (e: unknown) {
                const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
                const msg = Array.isArray(detail) ? (detail[0]?.msg ?? 'Sign-up failed.') : typeof detail === 'string' ? detail : 'Sign-up failed. Please try again.';
                setError(msg);
              } finally {
                setLoading(false);
              }
            }}
            style={{ background: BLUE, height: 44, borderRadius: 8, fontWeight: 600, fontSize: 14, marginTop: 8 }}
          >
            {mode === 'signin' ? 'Sign in' : 'Create Account'}
          </Button>
        </Stack>

        <Group gap={0} mt={24} mb={24} align="center">
          <Box style={{ flex: 1, height: 1, background: BORDER }} />
          <Text size="11px" c={TEXT_MUTED} px={12}>or continue with</Text>
          <Box style={{ flex: 1, height: 1, background: BORDER }} />
        </Group>

        <Box style={{ display: 'flex', justifyContent: 'center' }}>
          <GoogleLogin
            onSuccess={async (resp) => {
              if (!resp.credential) return;
              setLoading(true);
              setError('');
              try {
                const me = await googleLogin(resp.credential);
                await routeAfterLogin(me);
              } catch (e: unknown) {
                const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
                setError(typeof detail === 'string' ? detail : 'Google sign-in failed. Please try again.');
              } finally {
                setLoading(false);
              }
            }}
            onError={() => setError('Google sign-in was cancelled or failed. Please try again.')}
            theme="filled_black"
            size="large"
            width="360"
            text="signin_with"
          />
        </Box>

        <Text size="xs" c="#3F3F46" ta="center" mt={32}>
          HAIST &copy; {new Date().getFullYear()}
        </Text>
      </Box>

      <style>{`
        @media (max-width: 768px) {
          .login-brand-panel { display: none !important; }
        }
      `}</style>
    </Box>
  );
}
